import * as THREE from "three";
import { BoneAtlas } from "../../boneAtlas";
import { HQ_TROOP, lib } from "../../views/common";
import type { AssetLibrary } from "../../assets";
import { PET_KITS, TOWER_KITS, TROOP_KITS, ROAD_KITS } from "../kits";
import { STAND_IN_PET, petModelName } from "../kits/pets";
import { KitBodyMaterials } from "./bodyMaterial";
import "./modelSurface";
import { KitMaterials, rowsCharge } from "./kitMaterial";
import { KitPieces } from "./kitPieces";
import { buildKitGeometry, usesFor, type BoneMount, type KitPiece } from "./kitGeometry";
import { socketsFor, type SocketFrame } from "./sockets";
import { canonicalPart, partOf } from "./slots";
import { KitClock } from "./clock";
import { dimsFor, type FitDims } from "./fit";
import { hideableMask, kitHideMask, towerOfModel } from "./hide";
import { chargeShot, chargeTick, newChargeState, strikeNow, type ChargeState } from "./charge";
import type { SocketName, TowerKit } from "./types";

/** A tower model's node name: `tower_<id>_t<n>`, a tier-IV branch `…_t4_<branch>`, a skin's own model `…__<model>`. */
export const TOWER_MODEL_NODE = /^tower_[a-z]+_t\d(?:_[a-z]+)?(?:__[a-z0-9_]+)?$/;

/** Which of a kit's layers go on a model: a skin's own model takes `onModel`, a tier-IV branch needs `tier4`. */
export function kitLayers(kit: TowerKit, model: string): { slots: boolean; attachments: boolean; hide: boolean } {
  if (model.includes("__")) return { slots: kit.onModel?.slots === true, attachments: kit.onModel?.attachments === true, hide: kit.onModel?.hide === true };
  const branch = /_t4_/.test(model);
  return { slots: true, attachments: !branch || kit.tier4 === true, hide: !branch || kit.tier4 === true };
}

/** What a dressed tower keeps for the kit's effects: its sockets and the bones they ride. */
interface Dressing { readonly kit: TowerKit; readonly tower: string; readonly frames: Readonly<Record<SocketName, SocketFrame>>; readonly bones: readonly THREE.Object3D[]; readonly mountOf: (part: string) => BoneMount }
/** What a dressed tower keeps for its attack-synced charge (Motion.charge, Flap.drive): the uniforms and the timing state. */
interface ChargeRef { charge: { value: number }; strike: { value: number }; state: ChargeState }

/**
 * The skin kit at run time (kit/types.ts says what a kit is). `dressTower` puts a tower kit on a freshly cloned tower
 * model: the slot materials on its body, and its attachments as ONE extra skinned mesh bound to the tower's own
 * skeleton (the rigid rig's bones: BoneAtlas.join), so the props ride the build rise, the tier-up and the fire clip
 * with no per-frame work on the CPU; their ambient motion is the material's (kit/kitMaterial.ts).
 *
 * The kit's GLB loads on demand (it is in the screen's asset set when the skin is worn: render/assetNeeds.ts); a
 * tower dressed before it arrives wears the materials at once and gets its props when it lands (`onKitLoaded`).
 */
/** A pet model's far LOD (art/unitkit/pack_pet.sh: tools/art/unit_lod.mjs at 5 k triangles). */
export const petLodName = (model: string): string => `${model}_lod1`;

export class SkinKits {
  /** Set by WornSkins: re-dress what is on screen once a kit GLB has arrived. */
  static onKitLoaded: (() => void) | null = null;
  private static readonly geoCache = new Map<string, { geo: THREE.BufferGeometry | null; sphere: THREE.Sphere }>();

  static towerKit(skin: string | null | undefined): TowerKit | null { return (skin ? TOWER_KITS[skin] : undefined) ?? null; }

  /** The model name of a tower clone (`tower_archer_t2`), from its node names. */
  static modelOf(root: THREE.Object3D): string | null {
    let found: string | null = null;
    root.traverse((o) => { if (!found && TOWER_MODEL_NODE.test(o.name)) found = o.name; });
    return found;
  }

  /**
   * Dresses (or with null undresses) a tower model clone in a kit: body materials (with the stock parts the kit hides)
   * and the attachment mesh. On a skin's own model (`…__<model>`) only the layers `onModel` asks for; on a tier-IV
   * branch model the attachments and hiding only with `tier4` (kitLayers).
   */
  static dressTower(top: THREE.Object3D, archId: string, kit: TowerKit | null): void {
    // the clone itself (the glTF scene the model node sits in), whether we were handed it or a group holding it
    const root = SkinKits.hostOf(top);
    SkinKits.removeAttachments(root);
    delete root.userData.tdKitDressing;
    const mount = kit ? SkinKits.mountInfo(root) : null;
    const layers = kit && mount ? kitLayers(kit, mount.model) : { slots: kit !== null, attachments: false, hide: false };
    const hidden = kit && mount && layers.hide ? kitHideMask(kit, archId) & mount.hideable : 0;
    KitBodyMaterials.dress(top, kit && (layers.slots || hidden) ? (layers.slots ? kit : SkinKits.bare(kit)) : null, hidden);
    if (!kit || !mount) return;
    const { model, skeleton, frames, mountOf, dims } = mount;
    const tier = Number(/_t(\d)/.exec(model)?.[1] ?? 1);
    const L = lib();
    // a tier-IV branch model wears a classic kit's materials only (no props, no idle or flourish), as before big kits
    if (!layers.attachments && !layers.hide && !model.includes("__")) return;
    root.userData.tdKitDressing = { kit, tower: archId, frames, bones: skeleton.bones, mountOf } satisfies Dressing;
    if (!kit.asset || !L || !layers.attachments) return;
    const pieces = SkinKits.pieces(L, kit.asset);
    if (!pieces) return;
    const key = `${kit.id}|${model}`;
    let made = SkinKits.geoCache.get(key);
    if (!made) {
      const box = new THREE.Box3();
      const geo = buildKitGeometry(usesFor(kit.attachments, archId, tier, hidden), pieces, frames, mountOf, tier, box, dims);
      made = { geo, sphere: box.isEmpty() ? new THREE.Sphere() : box.getBoundingSphere(new THREE.Sphere()) };
      made.sphere.radius *= SkinKits.CullPad;
      SkinKits.geoCache.set(key, made);
    }
    if (!made.geo) return;
    const first = pieces.values().next().value as KitPiece;
    const rows = kit.attachments.map((u) => ({ motion: u.motion ?? pieces.get(u.piece)?.motion, detail: u.detail }));
    const shared = KitMaterials.variant(first.material, kit.id, rows);
    // a kit whose props answer the attack gets a copy of its material per tower (same program) to carry the charge
    const own = rowsCharge(rows) ? KitMaterials.perTower(shared) : null;
    const mesh = new THREE.SkinnedMesh(made.geo, own?.material ?? shared);
    if (own) root.userData.tdKitCharge = { charge: own.charge, strike: own.strike, state: (root.userData.tdKitCharge as ChargeRef | undefined)?.state ?? newChargeState() } satisfies ChargeRef;
    mesh.name = `${kit.id}_kit`;
    mesh.userData.tdKit = true;
    mesh.castShadow = false;               // small props: the tower's own shadow carries them (and no depth pass of the motion)
    mesh.receiveShadow = true;
    mesh.bind(skeleton, new THREE.Matrix4());
    mesh.boundingSphere = made.sphere.clone();
    mesh.onBeforeRender = () => KitClock.tick();
    root.add(mesh);
    BoneAtlas.join(skeleton, mesh);
  }

  /**
   * A tower clone's sockets and the bones they ride (measured once per clone, at rest): what `dressTower` mounts on,
   * and what the debug socket markers show (skinThumb.ts). Null for a model that is not a rigid tower.
   */
  static mountInfo(root: THREE.Object3D): { model: string; skeleton: THREE.Skeleton; frames: Readonly<Record<SocketName, SocketFrame>>; mountOf: (part: string) => BoneMount; dims: FitDims; hideable: number } | null {
    const cached = root.userData.tdKitMount as ReturnType<typeof SkinKits.mountInfo> | undefined;
    if (cached) return cached;
    const model = SkinKits.modelOf(root);
    const skeleton = SkinKits.skeletonOf(root);
    if (!model || !skeleton) return null;
    const box = SkinKits.restBox(root), anchor = lib()?.anchors.get(model);
    const frames = socketsFor(anchor?.sockets, box);
    // the stock groups this model can hide: its parts' (kit/hide.ts), as the bones name them
    const hideable = hideableMask(towerOfModel(model), skeleton.bones.map((b) => canonicalPart(partOf(b, model))));
    const info = { model, skeleton, frames, mountOf: SkinKits.mounts(root, model, skeleton), dims: dimsFor(anchor?.dims, box), hideable };
    root.userData.tdKitMount = info;
    return info;
  }

  /** World position of any socket of a tower clone (debug markers), worn kit or not. */
  static socketAt(top: THREE.Object3D, name: SocketName, out: THREE.Vector3): THREE.Vector3 | null {
    const m = SkinKits.mountInfo(SkinKits.hostOf(top));
    const f = m?.frames[name];
    if (!m || !f) return null;
    const b = m.mountOf(f.part);
    return out.set(f.p[0], f.p[1], f.p[2]).applyMatrix4(b.toLocal).applyMatrix4(m.skeleton.bones[b.index].matrixWorld);
  }

  /** Rest-pose bounds grown by this for culling (motion swings props past their rest pose). */
  static readonly CullPad = 1.3;

  /**
   * The object a tower's attachments hang from: the clone the model node `tower_<id>_t<n>` sits in (its parent),
   * found from the clone itself or from a group holding it (the views' `storeys`). Mount points are measured in this
   * object's space, which the views turn by HQ_YAW: hanging them anywhere else would mirror them.
   */
  static hostOf(top: THREE.Object3D): THREE.Object3D {
    let node: THREE.Object3D | null = null;
    top.traverse((o) => { if (!node && TOWER_MODEL_NODE.test(o.name)) node = o; });
    return (node as THREE.Object3D | null)?.parent ?? top;
  }

  /** A kit with its hiding but none of its materials (a skin's own model that asks for `onModel.hide` alone). */
  private static bare(kit: TowerKit): TowerKit { return { ...kit, id: `${kit.id}#bare`, slots: {} }; }

  /** A shot by a dressed tower: its round leaves the clip `release` s from now (the charge peaks then). */
  static chargeShot(top: THREE.Object3D, release: number): void {
    const c = SkinKits.chargeRef(top);
    if (c) chargeShot(c.state, release);
  }

  /** Advances a dressed tower's charge (every frame from the tower views); nothing for a kit without one. */
  static chargeTick(top: THREE.Object3D, dt: number): void {
    const c = SkinKits.chargeRef(top);
    if (c) { c.charge.value = chargeTick(c.state, dt); c.strike.value = strikeNow(c.state); }
  }

  /** The charge a tower's kit carries (found once per storeys group, then cached on it), or null. */
  private static chargeRef(top: THREE.Object3D): ChargeRef | null {
    let host = top.userData.tdKitHost as THREE.Object3D | undefined;
    if (!host) { host = SkinKits.hostOf(top); top.userData.tdKitHost = host; }
    return (host.userData.tdKitCharge as ChargeRef | undefined) ?? null;
  }

  /** World position of a socket on a dressed tower (+ `offset` in its frame; for the VFX), or null without a kit. */
  static socketWorld(top: THREE.Object3D, name: SocketName, out: THREE.Vector3, offset?: readonly [number, number, number]): THREE.Vector3 | null {
    const d = SkinKits.hostOf(top).userData.tdKitDressing as Dressing | undefined;
    const f = d?.frames[name];
    if (!d || !f) return null;
    const m = d.mountOf(f.part);
    const bone = d.bones[m.index];
    if (!bone) return null;
    const [r, u, o] = offset ?? [0, 0, 0];             // in the socket's frame (right, up, out), as a use's offset
    out.set(f.p[0] + r * f.right[0] + u * f.up[0] + o * f.out[0], f.p[1] + r * f.right[1] + u * f.up[1] + o * f.out[1], f.p[2] + r * f.right[2] + u * f.up[2] + o * f.out[2]);
    return out.applyMatrix4(m.toLocal).applyMatrix4(bone.matrixWorld);
  }

  /** The kit a dressed tower wears, or null. */
  static dressedKit(top: THREE.Object3D): TowerKit | null { return (SkinKits.hostOf(top).userData.tdKitDressing as Dressing | undefined)?.kit ?? null; }

  /** The tower type a dressed tower was dressed as (the kit's per-tower fits), or null. */
  static dressedTower(top: THREE.Object3D): string | null { return (SkinKits.hostOf(top).userData.tdKitDressing as Dressing | undefined)?.tower ?? null; }

  /**
   * The asset names the worn skins need loaded (kit GLBs, troop models, the pet that comes along and its worn skin's
   * model): render/assetNeeds.ts adds them to a screen's set.
   */
  static assetNames(worn: { towers: ReadonlyMap<string, string>; troop: string | null; road: string | null; pet?: string | null; pets?: ReadonlyMap<string, string>; petsOwned?: readonly string[] }): string[] {
    const out = new Set<string>();
    // the pet that comes along, and (the hub's lawn) every pet had, each in its worn coat
    for (const id of new Set([...(worn.pet ? [worn.pet] : []), ...(worn.petsOwned ?? [])])) for (const n of SkinKits.petAssetNames(id, worn.pets?.get(id) ?? null)) { out.add(n); out.add(petLodName(n)); }
    for (const id of worn.towers.values()) { const a = TOWER_KITS[id]?.asset; if (a) out.add(a); }
    const troop = worn.troop ? TROOP_KITS[worn.troop] : undefined;
    if (troop) { out.add(troop.model); out.add(`${troop.model}_lod1`); }
    const road = worn.road ? ROAD_KITS[worn.road]?.asset : null;
    if (road) out.add(road);
    return [...out];
  }

  /**
   * The models pet `petId` in `skin` may be drawn with (Pets v3): the coat's own, the pet's stock model, and — while the
   * pet's own model has not shipped — the stand-in pet's (docs/PETS.md: the art arrives pet by pet).
   */
  static petAssetNames(petId: string, skin: string | null): string[] {
    const kit = skin ? PET_KITS[skin] : undefined, own = petModelName(petId), out = [own];
    if (kit && kit.pet === petId) out.unshift(kit.model);
    if (petId !== STAND_IN_PET && !lib()?.ships(own)) out.push(petModelName(STAND_IN_PET));
    return out;
  }

  /**
   * The model a pet body drawn as `name` clones: its far LOD (`<name>_lod1`, ~5 k triangles, the same nodes and clips)
   * when `far` and that LOD is loaded, else `name` itself. The far battle zoom and the hub's pets that stay home ask
   * for it; the screens' asset sets load it beside the full model (assetNames, render/assetNeeds.ts).
   */
  static petDrawn(name: string, far: boolean): string {
    const lod = petLodName(name);
    return far && lib()?.hq(lod) ? lod : name;
  }

  /** The soldier model for a troop skin: its own GLB when it ships, else the stock soldier. */
  static troopModel(skin: string | null): string {
    const kit = skin ? TROOP_KITS[skin] : undefined;
    const L = lib();
    return kit && L?.ships(kit.model) ? kit.model : HQ_TROOP;
  }

  /**
   * The model pet `petId` in `skin` is drawn with: the first of its candidates (petAssetNames: the coat's own, the pet's
   * stock, the stand-in's) that is loaded, else the first that ships, else the pet's stock name (no library: tests).
   */
  static petModel(petId: string, skin: string | null): string {
    const L = lib(), names = SkinKits.petAssetNames(petId, skin);
    if (!L) return petModelName(petId);
    return names.find((n) => L.hq(n)) ?? names.find((n) => L.ships(n)) ?? petModelName(petId);
  }

  /**
   * The pieces of a kit GLB (kit/kitPieces.ts), or null until it is loaded — asking starts the load, and the worn
   * skins are re-dressed when it lands (`onKitLoaded`).
   */
  static pieces(L: AssetLibrary, asset: string): Map<string, KitPiece> | null {
    return KitPieces.of(L, asset, () => SkinKits.onKitLoaded?.());
  }

  private static removeAttachments(root: THREE.Object3D): void {
    for (const c of [...root.children]) {
      if (c.userData.tdKit !== true) continue;
      const m = c as THREE.SkinnedMesh;
      if (m.skeleton) BoneAtlas.leave(m.skeleton, m);
      KitMaterials.release(m.material as THREE.Material);
      root.remove(c);
    }
    const charge = root.userData.tdKitCharge as ChargeRef | undefined;
    if (charge) { charge.charge.value = 0; charge.strike.value = 0; }
    delete root.userData.tdKitCharge;
  }

  private static skeletonOf(root: THREE.Object3D): THREE.Skeleton | null {
    let s: THREE.Skeleton | null = null;
    root.traverse((o) => { const m = o as THREE.SkinnedMesh; if (!s && m.isSkinnedMesh && m.userData.tdKit !== true) s = m.skeleton; });
    return s;
  }

  /** The tower's box at rest in its own (root) space, from the rigid rig's measured bounds. */
  private static restBox(root: THREE.Object3D): { min: number[]; max: number[] } {
    const box = new THREE.Box3();
    root.traverse((o) => { const m = o as THREE.SkinnedMesh; if (m.isSkinnedMesh && m.userData.tdKit !== true && m.geometry.boundingBox) box.union(m.boundingBox ?? m.geometry.boundingBox); });
    return box.isEmpty() ? { min: [-0.4, 0, -0.4], max: [0.4, 1.8, 0.4] } : { min: box.min.toArray(), max: box.max.toArray() };
  }

  /** Per part: the bone a socket on it rides and the matrix into that bone's space, measured on the clone at rest. */
  private static mounts(root: THREE.Object3D, model: string, skeleton: THREE.Skeleton): (part: string) => BoneMount {
    root.updateMatrixWorld(true);
    const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
    // Every bone measured NOW, with the root: a part first asked for later (a kit GLB landing on a hub tower long
    // after it was built) found the clone moved under its stand and the root's matrix stale, and baked that offset
    // into the kit geometry every copy of the model shares — the props drew a few cells away from their tower.
    const rest = skeleton.bones.map((b) => new THREE.Matrix4().multiplyMatrices(toRoot, b.matrixWorld).invert());
    const cache = new Map<string, BoneMount>();
    const find = (part: string) => skeleton.bones.findIndex((b) => partOf(b, model) === part);
    return (part: string) => {
      let m = cache.get(part);
      if (m) return m;
      let i = find(part);
      if (i < 0) i = find("drum");
      if (i < 0) i = find("body");
      if (i < 0) i = 0;
      m = { index: i, toLocal: rest[i] };
      cache.set(part, m);
      return m;
    };
  }
}
